// Cube Analysis section for the Inbound Overview page: cube metric cards
// (Pending Depart, In Transit, Pending Induct, Pending Stow, Stow Cube) with
// package-size-distribution modals, plus the Linehaul Details grid.
import React, {useState} from 'react';
import {injectIntl} from "react-intl";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Modal from "@amzn/meridian/modal";
import LinehaulDetailsGrid from "./LinehaulDetailsGrid";
import './Inbound.css';

/**
 * Parses one LINEHAUL_DETAILS entry's pipe-delimited displayName into a row.
 * Two shapes per the v2 contract:
 *   Pending Depart: "VRID|cube|pkgCount|plannedTime"            (4 parts, no %XS/S)
 *   In Transit:     "VRID|cube|pkgCount|%XS/S|plannedTime"      (5 parts)
 */
function parseLinehaulDetail(entry) {
    const parts = String(entry.displayName || '').split('|');
    const lineHaulId = parts[0] || entry.name;
    const cube = parts.length > 1 ? parseFloat(parts[1]) : (typeof entry.value === 'number' ? entry.value : NaN);
    const packageCount = parts.length > 2 ? parseInt(parts[2], 10) : 0;

    let percentExtraSmallAndSmall = NaN;
    let plannedArrival = null;
    if (parts.length >= 5) {
        percentExtraSmallAndSmall = parseInt(String(parts[3]).replace('%', ''), 10);
        plannedArrival = parts[4];
    } else if (parts.length === 4) {
        plannedArrival = parts[3];
    }

    return {
        lineHaulId: lineHaulId,
        cube: isNaN(cube) ? null : cube,
        packageCount: isNaN(packageCount) ? 0 : packageCount,
        percentExtraSmallAndSmall: isNaN(percentExtraSmallAndSmall) ? null : percentExtraSmallAndSmall,
        plannedArrival: plannedArrival
    };
}

/**
 * Parses one ARRIVED_COUNT_RECEIVED_COUNT entry ("Arrived|7371|0.31") into { count, cube }.
 */
function parseStatusEntry(entry) {
    const parts = String(entry.displayName || '').split('|');
    const count = parts.length > 1 ? parseInt(parts[1], 10) : (entry.value || 0);
    const cube = parts.length > 2 ? parseFloat(parts[2]) : NaN;
    return {
        count: isNaN(count) ? 0 : count,
        cube: isNaN(cube) ? null : cube
    };
}

/**
 * Parses the GetMetricData API response into a structured format for rendering.
 *
 * Everything the Cube Analysis section needs — overall cube, package count, cycle
 * breakdown, per-linehaul details (LINEHAUL_DETAILS), the Arrived/Received breakdown
 * (ARRIVED_COUNT_RECEIVED_COUNT), and size distribution — comes from this single API.
 * No separate linehaul-list call is made.
 */
function parseApiResponse(apiData) {
    if (!apiData || !apiData.metricResults) return null;

    const metrics = {};
    apiData.metricResults.forEach(function(result) {
        const metricName = result.metric && result.metric.metricName;
        if (!metricName) return;

        const parsed = {
            value: 0,
            cycles: {},
            packageCount: 0,
            sizeDistribution: null,
            linehaulDetails: [],
            statusBreakdown: null
        };

        (result.metricValues || []).forEach(function(mv) {
            if (mv.name === 'AVG_CUBE') {
                parsed.value = mv.value || 0;
            } else if (mv.name === 'PACKAGE_COUNT') {
                parsed.packageCount = mv.value || 0;
            } else if (mv.name === 'CYCLE_BREAKDOWN') {
                (mv.values || []).forEach(function(entry) {
                    parsed.cycles[entry.displayName || entry.name] = entry.value;
                });
            } else if (mv.name === 'SIZE_DISTRIBUTION') {
                parsed.sizeDistribution = (mv.values || []).map(function(entry) {
                    return { label: entry.displayName || entry.name, count: entry.value || 0 };
                });
            } else if (mv.name === 'LINEHAUL_DETAILS') {
                parsed.linehaulDetails = (mv.values || []).map(parseLinehaulDetail);
            } else if (mv.name === 'ARRIVED_COUNT_RECEIVED_COUNT') {
                const breakdown = { arrived: null, received: null };
                (mv.values || []).forEach(function(entry) {
                    if (entry.name === 'ARRIVED') {
                        breakdown.arrived = parseStatusEntry(entry);
                    } else if (entry.name === 'RECEIVED') {
                        breakdown.received = parseStatusEntry(entry);
                    }
                });
                parsed.statusBreakdown = breakdown;
            }
        });

        metrics[metricName] = parsed;
    });

    return {
        pendingDepart: metrics['PENDING_DEPART_CUBE'] || null,
        inTransit: metrics['IN_TRANSIT_CUBE'] || null,
        pendingInduct: metrics['PENDING_INDUCT_CUBE'] || null,
        pendingStow: metrics['PENDING_STOW_CUBE'] || null,
        stowCube: metrics['STOW_CUBE'] || null
    };
}

const SizeDistributionTable = ({sizeDistribution}) => {
    if (!sizeDistribution || sizeDistribution.length === 0) return null;
    const total = sizeDistribution.reduce(function(sum, row) { return sum + row.count; }, 0);
    return (
        <div className="cube-size-distribution-table">
            {sizeDistribution.map((row, idx) => (
                <div key={idx} className="cube-size-distribution-row">
                    <span className="cube-size-label">{row.label}</span>
                    <span className="cube-size-value">{row.count.toLocaleString()}</span>
                    <span className="cube-size-percent">{total > 0 ? Math.round((row.count / total) * 100) : 0}%</span>
                </div>
            ))}
        </div>
    );
};

const PackagesPendingButton = ({count, label, onClick}) => (
    <button type="button" className="cube-packages-pending-btn" onClick={onClick}>
        {count.toLocaleString()} {label} <span className="cube-caret">&#9660;</span>
    </button>
);

const CubeCard = ({label, value, cycles, footer, note, descLabel}) => (
    <Box type="outline" spacingInset="300" className="cube-card-box">
        <div className="cube-card-top">
            <div className="cube-card-redesign">
                <div className="cube-card-left-value">
                    <div className="cube-card-label">{label}</div>
                    <div className="cube-card-value">{typeof value === 'number' ? value.toFixed(2) : value}</div>
                    <div className="cube-card-desc">{descLabel}</div>
                </div>
                <div className="cube-card-vline" />
                <div className="cube-card-cycles">
                    {Object.entries(cycles).map(([key, val]) => (
                        <div key={key}>{key}: {typeof val === 'number' ? val.toFixed(2) : val}</div>
                    ))}
                </div>
            </div>
            <div className="cube-card-note">{note || ' '}</div>
        </div>
        {footer && <div className="cube-card-footer">{footer}</div>}
    </Box>
);

/**
 * Cube Analysis Section — displays 5 cube metric cards (Pending Depart, In Transit,
 * Pending Induct, Pending Stow, Stow Cube) with cycle breakdowns, plus the Linehaul
 * Details grid beneath them. Everything is derived from the single GetMetricData
 * response passed via the cubeMetrics prop. On a fetch failure with no data to show,
 * surfaces an error message (consistent with StowMetricsSection).
 */
const CubeAnalysisSection = ({cubeMetrics, cubeMetricsError, intl}) => {
    const [modalCard, setModalCard] = useState(null);
    const t = (id, defaultMessage, values) => intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);

    const data = parseApiResponse(cubeMetrics);

    // Feature disabled / initial load with nothing yet and no error — render nothing.
    if (!data && !cubeMetricsError) return null;

    // Fetch failed and we have no (stale) data to show — surface an error.
    if (!data) {
        return (
            <div>
                <h2 className="cube-analysis-title">{t('inbound.cube_analysis', 'Cube Analysis')}</h2>
                <div className="error-message">{t('inbound.cube_metrics_error', 'Unable to load cube metrics')}</div>
            </div>
        );
    }

    const openModal = (cardKey) => setModalCard(cardKey);
    const closeModal = () => setModalCard(null);

    const modalData = modalCard ? data[modalCard] : null;
    const modalTitle = modalCard === 'pendingInduct' ? t('inbound.cube.modal_pending_induct', 'Pending Induct — Package Size Distribution')
        : modalCard === 'pendingStow' ? t('inbound.cube.modal_pending_stow', 'Pending Stow — Package Size Distribution')
        : modalCard === 'stowCube' ? t('inbound.cube.modal_stow_cube', 'Stow Cube — Package Size Distribution')
        : '';

    const cuft = t('inbound.cube.cuft', 'Cu.ft');
    const cutoffNote = t('inbound.cube.fixed_cutoff_note', 'Fixed 24hr cutoff · unaffected by time filter');

    // Linehaul Details grid is driven entirely by the cube response's sub-values.
    const linehaulData = {
        pendingDepart: data.pendingDepart ? data.pendingDepart.linehaulDetails : [],
        inTransit: data.inTransit ? data.inTransit.linehaulDetails : [],
        arrivedReceived: data.pendingInduct ? data.pendingInduct.statusBreakdown : null
    };

    return (
        <div>
            <h2 className="cube-analysis-title">{t('inbound.cube_analysis', 'Cube Analysis')}</h2>
            <div className="cube-breakdown-title">{t('inbound.cube.current_breakdown', 'Current Cube Breakdown')}</div>
            <Row widths={["20%", "20%", "20%", "20%", "20%"]} wrap="down" alignmentVertical="stretch">
                {data.pendingDepart && (
                    <CubeCard
                        label={t('inbound.cube.pending_depart', 'Pending Depart')}
                        value={data.pendingDepart.value}
                        cycles={data.pendingDepart.cycles}
                        descLabel={cuft}
                        note={cutoffNote}
                        footer={data.pendingDepart.packageCount > 0 &&
                            data.pendingDepart.packageCount.toLocaleString() + ' ' + t('inbound.cube.packages_pending_depart', 'packages pending depart')}
                    />
                )}
                {data.inTransit && (
                    <CubeCard
                        label={t('inbound.cube.in_transit', 'In Transit')}
                        value={data.inTransit.value}
                        cycles={data.inTransit.cycles}
                        descLabel={cuft}
                        note={cutoffNote}
                        footer={data.inTransit.packageCount > 0 &&
                            data.inTransit.packageCount.toLocaleString() + ' ' + t('inbound.cube.packages_in_transit', 'packages in transit')}
                    />
                )}
                {data.pendingInduct && (
                    <CubeCard
                        label={t('inbound.cube.pending_induct', 'Pending Induct')}
                        value={data.pendingInduct.value}
                        cycles={data.pendingInduct.cycles}
                        descLabel={cuft}
                        footer={data.pendingInduct.packageCount > 0 && (
                            <PackagesPendingButton
                                count={data.pendingInduct.packageCount}
                                label={t('inbound.cube.packages_pending', 'packages pending')}
                                onClick={() => openModal('pendingInduct')}
                            />
                        )}
                    />
                )}
                {data.pendingStow && (
                    <CubeCard
                        label={t('inbound.cube.pending_stow', 'Pending Stow')}
                        value={data.pendingStow.value}
                        cycles={data.pendingStow.cycles}
                        descLabel={cuft}
                        footer={data.pendingStow.packageCount > 0 && (
                            <PackagesPendingButton
                                count={data.pendingStow.packageCount}
                                label={t('inbound.cube.packages_remaining', 'packages remaining')}
                                onClick={() => openModal('pendingStow')}
                            />
                        )}
                    />
                )}
                {data.stowCube && (
                    <CubeCard
                        label={t('inbound.cube.stow_cube', 'Stow Cube')}
                        value={data.stowCube.value}
                        cycles={data.stowCube.cycles}
                        descLabel={cuft}
                        footer={data.stowCube.packageCount > 0 && (
                            <PackagesPendingButton
                                count={data.stowCube.packageCount}
                                label={t('inbound.cube.packages_stowed', 'packages stowed')}
                                onClick={() => openModal('stowCube')}
                            />
                        )}
                    />
                )}
            </Row>
            <LinehaulDetailsGrid linehaulData={linehaulData} />
            <Modal
                open={modalCard !== null}
                onClose={closeModal}
                title={modalTitle}
                closeLabel={t('inbound.cube.modal_close', 'Close')}
                width={420}
            >
                {modalData && modalData.sizeDistribution && modalData.sizeDistribution.length > 0 ? (
                    <SizeDistributionTable sizeDistribution={modalData.sizeDistribution} />
                ) : (
                    <div className="cube-size-distribution-empty">{t('inbound.cube.no_size_distribution', 'No size distribution data available')}</div>
                )}
            </Modal>
        </div>
    );
};

export default injectIntl(CubeAnalysisSection);



// WEBPACK FOOTER //
// ./src/components/inbound/CubeAnalysisSection.js